import { ApiError } from '../api/client';
import type { AuthConfig, MeResponse } from '../types';
import { isTerminalApiAuthCode } from './authRecovery';
import { sanitizeRequestId } from './errors';

export const STARTUP_TIMEOUT_MS = 20_000;
export type StartupStage = 'config' | 'session' | 'profile';
export type StartupFailure = {
  kind: 'availability' | 'authentication' | 'authorization' | 'configuration' | 'response';
  title: string;
  message: string;
  requestId?: string;
};
export type StartupView =
  | { kind: 'loading'; stage: StartupStage }
  | { kind: 'failed'; failure: StartupFailure; retry: StartupStage };

export class StartupTimeoutError extends Error {}
export class StartupResponseError extends Error {}

// Use only structured status/code metadata. Response bodies and provider error
// descriptions can contain HTML, callback parameters or internal diagnostics.
export function startupFailure(error: unknown, stage: StartupStage): StartupFailure {
  const status = error instanceof ApiError ? error.status : undefined;
  const requestId = error instanceof ApiError ? sanitizeRequestId(error.requestId) : undefined;
  const providerCode = error && typeof error === 'object' && 'error' in error ? error.error : undefined;
  const timedOut = error instanceof StartupTimeoutError ||
    (error instanceof Error && (error.name === 'ErrorTimeout' || error.name === 'AbortError'));
  if (timedOut || error instanceof TypeError || status === 408 || status === 429 ||
      (status !== undefined && status >= 500) || providerCode === 'server_error' || providerCode === 'temporarily_unavailable') {
    return {
      kind: 'availability',
      title: stage === 'session' ? 'Sign-in service unavailable' : 'Service unavailable',
      message: timedOut
        ? 'The service took too long to respond. Check your connection and retry. If this continues, contact an administrator.'
        : 'The service could not be reached or is temporarily unavailable. Check your connection and retry. Signing in again may not help.',
      requestId
    };
  }
  if (status === 403) {
    return {
      kind: 'authorization',
      title: 'Access denied',
      message: 'The server refused access to this account. Ask an administrator to check your permissions, then retry the access check.',
      requestId
    };
  }
  if ((error instanceof ApiError && isTerminalApiAuthCode(error.code)) ||
      providerCode === 'invalid_client' || providerCode === 'invalid_request') {
    return {
      kind: 'configuration',
      title: 'Sign-in configuration needs attention',
      message: 'Sign-in could not establish API access. The sign-in service and application could not agree on access. Ask an administrator to check the sign-in configuration before retrying.',
      requestId
    };
  }
  if (status === 401 || stage === 'session') {
    return {
      kind: 'authentication',
      title: 'Sign-in required',
      message: 'Sign-in could not establish API access. Retry sign-in once. If this continues, ask an administrator to check your session and sign-in configuration. No automatic redirect will be repeated.',
      requestId
    };
  }
  return {
    kind: 'response',
    title: 'Unable to load the application',
    message: 'The service did not return a usable response. Retry loading. If this continues, contact an administrator.',
    requestId
  };
}

export function startupView({
  configLoaded, sessionReady, hasToken, hasUser, loginConfigured,
  configFailure, sessionFailure, profileFailure
}: {
  configLoaded: boolean;
  sessionReady: boolean;
  hasToken: boolean;
  hasUser: boolean;
  loginConfigured: boolean;
  configFailure: StartupFailure | null;
  sessionFailure: StartupFailure | null;
  profileFailure: StartupFailure | null;
}): StartupView | null {
  if (configFailure) return { kind: 'failed', failure: configFailure, retry: 'config' };
  if (!configLoaded) return { kind: 'loading', stage: 'config' };
  if (sessionFailure) return { kind: 'failed', failure: sessionFailure, retry: 'session' };
  if (!sessionReady) return { kind: 'loading', stage: 'session' };
  if (hasToken && profileFailure) {
    const needsSignIn = profileFailure.kind === 'authentication' || profileFailure.kind === 'configuration';
    if (!loginConfigured && profileFailure.kind === 'authentication') {
      return { kind: 'failed', retry: 'profile', failure: {
        ...profileFailure,
        message: 'The browser login credential was rejected. Ask an administrator for a new browser login link, or retry the access check after access is restored.'
      } };
    }
    return { kind: 'failed', failure: profileFailure, retry: needsSignIn && loginConfigured ? 'session' : 'profile' };
  }
  if (hasToken && !hasUser) return { kind: 'loading', stage: 'profile' };
  if (!hasToken && !loginConfigured) {
    return {
      kind: 'failed', retry: 'config',
      failure: {
        kind: 'configuration', title: 'Sign-in is not configured',
        message: 'Ask an administrator to configure browser sign-in or provide a valid browser login link, then retry loading.'
      }
    };
  }
  return null;
}

export function isAuthConfig(value: unknown): value is AuthConfig {
  return !!value && typeof value === 'object' && 'issuer' in value && typeof value.issuer === 'string' &&
    (!('client_id' in value) || value.client_id == null || typeof value.client_id === 'string');
}

export function isMeResponse(value: unknown): value is MeResponse {
  if (!value || typeof value !== 'object' || !('user' in value) || !('roles' in value)) return false;
  const user = value.user;
  return !!user && typeof user === 'object' && 'id' in user && typeof user.id === 'string' && !!user.id &&
    (!('name' in user) || user.name == null || typeof user.name === 'string') &&
    (!('email' in user) || user.email == null || typeof user.email === 'string') &&
    Array.isArray(value.roles) && value.roles.every(role => typeof role === 'string');
}

// One bounded attempt. Cleanup aborts transport and ignores late completions;
// only an explicit caller retry starts another attempt.
export function startStartupRequest<T>(
  load: (signal: AbortSignal) => Promise<T>,
  success: (value: T) => void,
  failure: (error: unknown) => void
): () => void {
  const controller = new AbortController();
  let active = true;
  const timer = setTimeout(() => {
    if (!active) return;
    active = false;
    controller.abort();
    failure(new StartupTimeoutError());
  }, STARTUP_TIMEOUT_MS);
  void Promise.resolve().then(async () => {
    if (!active) return;
    try {
      const value = await load(controller.signal);
      if (!active) return;
      active = false;
      clearTimeout(timer);
      success(value);
    } catch (error) {
      if (!active) return;
      active = false;
      clearTimeout(timer);
      failure(error);
    }
  });
  return () => {
    active = false;
    clearTimeout(timer);
    controller.abort();
  };
}
